import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TextParagraph } from '../model';

// #756 / #609 T2 -- the accepted 1-second typing burst: typing accumulates in a draft; one second
// of inactivity commits it as a single history step. Caret movement, formatting and leaving end a
// burst early (`flush`); Escape drops only the uncommitted burst (`cancel`). Earlier bursts are
// already in history, so they stay intact and undo separately.
export const TYPING_BURST_MS = 1000;

export interface TypingBursts {
  readonly draft: readonly TextParagraph[];
  readonly pending: boolean;
  // A typing edit: joins the current burst.
  readonly edit: (next: readonly TextParagraph[]) => void;
  // Commits the current burst, if any.
  readonly flush: () => void;
  // Ends the current burst, then commits `next` as its own step (formatting, alignment).
  readonly commitStep: (next: readonly TextParagraph[]) => void;
  // Discards the uncommitted burst, restoring the last committed text.
  readonly cancel: () => readonly TextParagraph[];
  // Replaces the draft with text that changed underneath (undo/redo); drops any pending burst.
  readonly reset: (next: readonly TextParagraph[]) => void;
  // While true (IME composition) the inactivity timer cannot commit.
  readonly hold: (held: boolean) => void;
}

export function useTypingBursts(
  initial: readonly TextParagraph[],
  onCommit: (paragraphs: readonly TextParagraph[]) => void
): TypingBursts {
  const [draft, setDraft] = useState(initial);
  const draftRef = useRef(initial);
  const committedRef = useRef(initial);
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);
  const heldRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  });

  const clearTimer = () => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const setDraftBoth = (next: readonly TextParagraph[]) => {
    draftRef.current = next;
    setDraft(next);
  };

  const setPendingBoth = (next: boolean) => {
    pendingRef.current = next;
    setPending(next);
  };

  const flush = useCallback(() => {
    clearTimer();
    if (!pendingRef.current) return;
    setPendingBoth(false);
    committedRef.current = draftRef.current;
    onCommitRef.current(draftRef.current);
  }, []);

  const arm = useCallback(() => {
    clearTimer();
    if (heldRef.current) return;
    timerRef.current = setTimeout(flush, TYPING_BURST_MS);
  }, [flush]);

  const edit = useCallback(
    (next: readonly TextParagraph[]) => {
      setDraftBoth(next);
      setPendingBoth(true);
      arm();
    },
    [arm]
  );

  const commitStep = useCallback(
    (next: readonly TextParagraph[]) => {
      flush();
      setDraftBoth(next);
      committedRef.current = next;
      onCommitRef.current(next);
    },
    [flush]
  );

  const cancel = useCallback(() => {
    clearTimer();
    setPendingBoth(false);
    setDraftBoth(committedRef.current);
    return committedRef.current;
  }, []);

  const reset = useCallback((next: readonly TextParagraph[]) => {
    clearTimer();
    setPendingBoth(false);
    committedRef.current = next;
    setDraftBoth(next);
  }, []);

  const hold = useCallback(
    (held: boolean) => {
      heldRef.current = held;
      if (held) clearTimer();
      else if (pendingRef.current) arm();
    },
    [arm]
  );

  useEffect(() => clearTimer, []);

  return useMemo(
    () => ({ draft, pending, edit, flush, commitStep, cancel, reset, hold }),
    [draft, pending, edit, flush, commitStep, cancel, reset, hold]
  );
}
